import React, { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Trophy, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar, 
  MapPin, 
  User,
  Loader2,
  AlertTriangle,
  Edit,
  Bell,
  Send,
  Mail
} from 'lucide-react';

export default function AdminResults() {
  const [loading, setLoading] = useState(true);
  const [pendingMatches, setPendingMatches] = useState([]);
  const [awaitingMatches, setAwaitingMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [editScores, setEditScores] = useState({ home: 0, away: 0 });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pendingRes, awaitingRes] = await Promise.all([
        adminAPI.getPendingResults(),
        adminAPI.getMatchesAwaitingResults()
      ]);
      setPendingMatches(pendingRes.data);
      setAwaitingMatches(awaitingRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Ergebnisse');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingResults = async () => {
    try {
      const response = await adminAPI.getPendingResults();
      setPendingMatches(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Ergebnisse');
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (match) => {
    setSelectedMatch(match);
    setEditScores({ home: match.home_score || 0, away: match.away_score || 0 });
    setShowConfirmDialog(true);
  };

  const openRejectDialog = (match) => {
    setSelectedMatch(match);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  const handleConfirm = async () => {
    if (!selectedMatch) return;
    
    setProcessing(true);
    try {
      const response = await adminAPI.confirmResult(selectedMatch.id, editScores.home, editScores.away);
      const data = response.data;
      toast.success(`Ergebnis bestätigt! ${data.stats_updated} Spieler-Statistiken aktualisiert.`);
      setShowConfirmDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Bestätigen');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedMatch) return;
    
    setProcessing(true);
    try {
      await adminAPI.rejectResult(selectedMatch.id, rejectReason || 'Ergebnis konnte nicht verifiziert werden');
      toast.success('Ergebnis abgelehnt - Host wurde benachrichtigt');
      setShowRejectDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ablehnen');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await adminAPI.triggerResultReminders();
      toast.success(`${response.data.reminded_matches} Erinnerungen gesendet!`);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Senden der Erinnerungen');
    } finally {
      setSendingReminders(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-tunis-blue flex items-center gap-2">
              <Trophy className="w-6 h-6 text-tunis-teal" />
              Spielergebnisse verwalten
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {pendingMatches.length} zur Bestätigung • {awaitingMatches.length} ohne Eingabe
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData}>
              <Clock className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
            {awaitingMatches.length > 0 && (
              <Button 
                onClick={handleSendReminders}
                disabled={sendingReminders}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {sendingReminders ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Erinnerungen senden
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Zur Bestätigung ({pendingMatches.length})
            </TabsTrigger>
            <TabsTrigger value="awaiting" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Fehlendes Ergebnis ({awaitingMatches.length})
            </TabsTrigger>
          </TabsList>

          {/* Tab: Pending Results */}
          <TabsContent value="pending" className="mt-4">
            {pendingMatches.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Alles erledigt!</h3>
                  <p className="text-muted-foreground">Keine ausstehenden Ergebnisse zur Bestätigung</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingMatches.map((match) => (
                  <Card key={match.id} className="border-l-4 border-l-amber-500">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Match Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-amber-100 text-amber-800">
                              <Clock className="w-3 h-3 mr-1" />
                              Ausstehend
                            </Badge>
                          </div>
                          
                          <h3 className="font-bold text-tunis-blue text-lg mb-2">
                            {match.field_name || 'Unbekanntes Feld'}
                          </h3>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {match.date}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {match.time}
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          Host: {match.host_name}
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Eingereicht von: {match.submitted_by_name} • {new Date(match.result_submitted_at).toLocaleString('de-DE')}
                      </p>
                    </div>

                    {/* Submitted Score */}
                    <div className="flex items-center justify-center gap-4 py-4 px-6 bg-slate-50 rounded-xl">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground mb-1">Heim</p>
                        <span className="text-4xl font-bold text-tunis-blue">{match.home_score}</span>
                      </div>
                      <span className="text-2xl font-bold text-slate-400">:</span>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground mb-1">Auswärts</p>
                        <span className="text-4xl font-bold text-tunis-blue">{match.away_score}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => openConfirmDialog(match)}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Bestätigen
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => openConfirmDialog(match)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Bearbeiten
                      </Button>
                      <Button 
                        variant="destructive"
                        onClick={() => openRejectDialog(match)}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Ablehnen
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>

          {/* Tab: Awaiting Results */}
          <TabsContent value="awaiting" className="mt-4">
            {awaitingMatches.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Keine fehlenden Ergebnisse</h3>
                  <p className="text-muted-foreground">Alle beendeten Spiele haben Ergebnisse</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Info Banner */}
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Bell className="w-5 h-5 text-amber-600" />
                    <div className="flex-1">
                      <p className="text-sm text-amber-800">
                        Diese Spiele sind beendet, aber der Host hat noch kein Ergebnis eingegeben. 
                        Erinnerungen werden automatisch alle 24 Stunden gesendet.
                      </p>
                    </div>
                    <Button 
                      size="sm"
                      onClick={handleSendReminders}
                      disabled={sendingReminders}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </CardContent>
                </Card>

                {awaitingMatches.map((match) => (
                  <Card key={match.id} className="border-l-4 border-l-red-400">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Match Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-red-100 text-red-800">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {match.hours_overdue}h überfällig
                            </Badge>
                          </div>
                          
                          <h3 className="font-bold text-tunis-blue text-lg mb-2">
                            {match.field_name || 'Unbekanntes Feld'}
                          </h3>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {match.date}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {match.time}
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {match.host_name}
                            </div>
                          </div>
                          
                          {match.host_email && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {match.host_email}
                            </p>
                          )}
                        </div>

                        {/* Status */}
                        <div className="flex items-center justify-center py-4 px-6 bg-red-50 rounded-xl">
                          <div className="text-center">
                            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-red-700">Kein Ergebnis</p>
                            {match.last_result_reminder && (
                              <p className="text-xs text-red-500 mt-1">
                                Letzte Erinnerung: {new Date(match.last_result_reminder).toLocaleString('de-DE')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Confirm Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ergebnis bestätigen</DialogTitle>
              <DialogDescription>
                Sie können das Ergebnis anpassen, bevor Sie es bestätigen. Nach der Bestätigung werden die ELO-Punkte automatisch aktualisiert.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <Label htmlFor="home-score" className="text-sm text-muted-foreground">Heim</Label>
                  <Input
                    id="home-score"
                    type="number"
                    min="0"
                    max="99"
                    value={editScores.home}
                    onChange={(e) => setEditScores({ ...editScores, home: parseInt(e.target.value) || 0 })}
                    className="w-20 text-center text-2xl font-bold mt-1"
                  />
                </div>
                <span className="text-2xl font-bold text-slate-400 mt-6">:</span>
                <div className="text-center">
                  <Label htmlFor="away-score" className="text-sm text-muted-foreground">Auswärts</Label>
                  <Input
                    id="away-score"
                    type="number"
                    min="0"
                    max="99"
                    value={editScores.away}
                    onChange={(e) => setEditScores({ ...editScores, away: parseInt(e.target.value) || 0 })}
                    className="w-20 text-center text-2xl font-bold mt-1"
                  />
                </div>
              </div>
              
              {editScores.home !== editScores.away && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  {editScores.home > editScores.away ? 'Heim-Team' : 'Auswärts-Team'} gewinnt • ELO wird angepasst
                </p>
              )}
              {editScores.home === editScores.away && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  Unentschieden • Keine ELO-Änderung
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleConfirm}
                disabled={processing}
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Bestätigen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Ergebnis ablehnen
              </DialogTitle>
              <DialogDescription>
                Der Spielersteller wird benachrichtigt und kann ein neues Ergebnis einreichen.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label htmlFor="reject-reason">Grund für Ablehnung (optional)</Label>
              <Input
                id="reject-reason"
                placeholder="z.B. Ergebnis stimmt nicht mit Spielprotokoll überein"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="mt-2"
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                variant="destructive"
                onClick={handleReject}
                disabled={processing}
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Ablehnen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
